// src/components/manuscript/previewRenderers.jsx
//
// What each kind of manuscript element looks like on the page.
//
// One renderer per `preview` value in the element catalogue.
//
// **Sizes are in `em`, and that is load-bearing.** The author picks a font size;
// `contentSx` below turns it into one `fontSize` on the wrapper, and everything
// inside scales from it by inheritance. A renderer that sets a px size opts that
// text out of the author's control, so only the things that are deliberately
// fixed relative to the page — nothing, currently — may do so.
//
// **Font family inherits too.** The wrapper carries the author's typeface, and
// prose simply does not name a family. The small monospace captions (a chapter
// number, a source attribution) do name one, because they read as metadata
// printed on the page rather than as the author's prose.
//
// These are presentational and take no callbacks: the preview is a picture of the
// template, never a second place to edit it.
import React from 'react';
import { Box } from '@mui/material';

import { fontStackFor } from '../../config/fonts';
import { SP, FONT } from './structureTokens';

// The body-text size on a leaf at this scale when the author has chosen 16px.
// Everything else is an `em` multiple of it. The leaf is roughly a third of a
// real page wide, so this is proportional rather than literal — a true 16px here
// would be about 5px on screen and unreadable.
const PREVIEW_BASE_PX = 11;
const AUTHOR_BASE_PX = 16;

/**
 * The author's formatting, as CSS for the wrapper that holds an element's page
 * content. Everything inside inherits from it.
 */
export function contentSx(format = {}) {
  const decoration = [
    format.underline ? 'underline' : '',
    format.strike ? 'line-through' : '',
  ].filter(Boolean).join(' ');

  const chosen = Number(format.fontSize) || AUTHOR_BASE_PX;

  return {
    fontFamily: fontStackFor(format.fontFamily),
    fontSize: `${(PREVIEW_BASE_PX * chosen) / AUTHOR_BASE_PX}px`,
    fontWeight: format.bold ? 700 : 400,
    fontStyle: format.italic ? 'italic' : 'normal',
    textDecoration: decoration || 'none',
    textAlign: format.align || 'left',
  };
}

/**
 * A line of page text.
 *
 * Deliberately NOT `Typography`. A bare `<Typography>` carries the `body1`
 * variant, and `body1` emits its own `font-family` and `font-weight` from the
 * MUI theme — Roboto at 400, since `theme.js` sets no `typography` key. An
 * emitted declaration beats an inherited one, so the author's font and bold,
 * written once onto the wrapper by `contentSx`, never reached the words: the
 * toolbar updated and the preview did not. The `Box component="span"` bits in
 * this file never had the problem, which is why the leader-row labels already
 * followed the author's font while the paragraphs beside them did not.
 *
 * The margin and line-height below are what `Typography` was contributing,
 * kept so this swap changes the font and nothing else. Anything that means to
 * escape the author's choice — `captionSx`, the mono labels — still names its
 * own family, and still wins.
 */
function Text({ sx, ...props }) {
  return <Box component="p" {...props} sx={{ m: 0, lineHeight: 1.5, ...sx }} />;
}

// A caption: metadata printed on the page, not the author's prose. Keeps its own
// family deliberately — see the header.
const captionSx = {
  fontFamily: FONT.mono,
  fontSize: '0.9em',
  fontWeight: 400,
  fontStyle: 'normal',
  color: SP.goldDeep,
  textTransform: 'uppercase',
  letterSpacing: '.05em',
};

const bodySx = {
  fontSize: '1em',
  lineHeight: 1.75,
  color: SP.black,
  opacity: 0.85,
  whiteSpace: 'pre-wrap',
};

const headingSx = {
  fontSize: '1.55em',
  fontWeight: 600,
  lineHeight: 1.2,
};

// Horizontal centring only. Vertical placement is the leaf's job — it applies the
// author's Top / Center / Bottom choice with `justify-content`, and a child that
// claimed `flex: 1` or `margin: auto` here would silently override it.
const centeredWrapSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: '85%',
  mx: 'auto',
};

/** A row of a contents-style list: label, leader dots, page number. */
function LeaderRow({ label, trailing, sub = false }) {
  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: 1,
      fontSize: '1.05em',
      color: SP.black,
      mb: 0.9,
      pl: sub ? '1.5em' : 0,
    }}>
      <Box component="span">{label}</Box>
      <Box component="span" sx={{
        flex: 1,
        mx: 0.75,
        mb: '4px',
        height: '1px',
        alignSelf: 'end',
        borderBottom: `1px dotted ${SP.lineSoft}`,
      }} />
      <Box component="span" sx={{ fontFamily: FONT.mono, color: SP.gray, flexShrink: 0 }}>
        {trailing}
      </Box>
    </Box>
  );
}

/**
 * The content of one element, drawn as it would appear on the page.
 *
 * Rendered inside a wrapper carrying `contentSx`, so nothing here sets a font
 * family or a px size unless it means to escape the author's choice.
 */
export default function PreviewContent({ element, state }) {
  const f = state.fields || {};

  switch (element.preview) {
    case 'legal':
      return (
        <Box>
          {[f.notice, f.isbn ? `ISBN ${f.isbn}` : '', f.edition, f.history]
            .filter(Boolean)
            .map((line, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Text key={i} sx={{ fontSize: '0.95em', color: SP.gray, lineHeight: 1.9 }}>
                {line}
              </Text>
            ))}
        </Box>
      );

    case 'centered':
      return (
        <Box sx={centeredWrapSx}>
          <Text sx={{ fontSize: '1.36em', lineHeight: 1.7, color: SP.black }}>
            {f.text}
          </Text>
        </Box>
      );

    case 'centered-quote':
      return (
        <Box sx={centeredWrapSx}>
          <Text sx={{ fontSize: '1.36em', lineHeight: 1.7, color: SP.purple, fontStyle: 'italic' }}>
            &ldquo;{f.quote}&rdquo;
          </Text>
          <Text sx={{ ...captionSx, color: SP.gray, letterSpacing: '.06em', mt: 1.75 }}>
            {f.source}
          </Text>
        </Box>
      );

    case 'list':
      return (
        <Box>
          <Text sx={{ ...headingSx, fontSize: '1.36em', mb: 1.75, textAlign: 'center' }}>
            {element.name}
          </Text>
          {(f.rows || []).map((row, i) => (
            <LeaderRow
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              label={row.title || row.caption || row.note || row.term || row.source || ''}
              trailing={row.page || row.chapter || row.year || ''}
              sub={row.level > 1}
            />
          ))}
        </Box>
      );

    case 'bodytext':
      return (
        <Box>
          <Text sx={{ ...headingSx, mb: 0.5 }}>
            {element.name.replace(' (Alternate)', '')}
          </Text>
          {(f.contributor || f.title) && (
            <Text sx={{ ...captionSx, mb: 1.5 }}>
              {f.contributor ? `by ${f.contributor}` : f.title}
            </Text>
          )}
          <Text sx={bodySx}>{f.body}</Text>
        </Box>
      );

    case 'chapter':
      return (
        <Box>
          {!element.chapterHeadingOnly && (
            <Text sx={{ ...captionSx, letterSpacing: '.1em' }}>{f.chapnum}</Text>
          )}
          <Text sx={{ ...headingSx, fontSize: '1.73em', mt: 0.5, mb: 1.75 }}>
            {element.chapterHeadingOnly ? f.heading : f.chaptitle}
          </Text>
          <Text sx={bodySx}>{f.body}</Text>
        </Box>
      );

    case 'glossary':
      return (
        <Box>
          <Text sx={{ ...headingSx, fontSize: '1.36em', mb: 1.75, textAlign: 'center' }}>
            Glossary
          </Text>
          {(f.rows || []).map((row, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={i} sx={{ mb: 1.15 }}>
              <Text sx={{ fontSize: '1.09em', fontStyle: 'italic', color: SP.purple }}>
                {row.term}
              </Text>
              <Text sx={{ fontSize: '0.95em', color: SP.black, opacity: 0.8, mt: '1px' }}>
                {row.definition}
              </Text>
            </Box>
          ))}
        </Box>
      );

    case 'authorbio':
      return (
        <Box sx={centeredWrapSx}>
          <Box sx={{
            width: 56,
            height: 56,
            flexShrink: 0,
            borderRadius: '50%',
            backgroundColor: SP.lavender,
            border: `1px dashed ${SP.lineSoft}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1.5,
          }}>
            <Box component="svg" viewBox="0 0 24 24" sx={{
              width: 24,
              height: 24,
              stroke: SP.purpleSoft,
              fill: 'none',
              strokeWidth: 1.5,
            }}>
              <circle cx="12" cy="8" r="4" />
              <path d="M4 20c0-4.4 3.6-7 8-7s8 2.6 8 7" />
            </Box>
          </Box>
          <Text sx={bodySx}>{f.bio}</Text>
        </Box>
      );

    case 'guide':
      return (
        <Box>
          <Text sx={{ ...headingSx, fontSize: '1.36em', mb: 1.75, textAlign: 'center' }}>
            Reading Group Guide
          </Text>
          {(f.rows || []).map((row, i) => (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              sx={{ display: 'flex', gap: 1, fontSize: '1em', mb: 1.15, lineHeight: 1.6 }}
            >
              <Box component="span" sx={{ fontFamily: FONT.mono, color: SP.goldDeep, flexShrink: 0 }}>
                {i + 1}.
              </Box>
              <Box component="span">{row.text}</Box>
            </Box>
          ))}
        </Box>
      );

    case 'booklist':
      return (
        <Box>
          <Text sx={{ fontSize: '0.95em', color: SP.gray, textAlign: 'center', mb: 1.5 }}>
            {f.blurb}
          </Text>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
            {(f.rows || []).map((row, i) => (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                sx={{
                  aspectRatio: '2 / 3',
                  backgroundColor: SP.lavender,
                  border: `1px solid ${SP.lineSoft}`,
                  borderRadius: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 0.6,
                  fontSize: '0.77em',
                  fontStyle: 'italic',
                  textAlign: 'center',
                  color: SP.purple,
                }}
              >
                {row.title}
              </Box>
            ))}
          </Box>
        </Box>
      );

    default:
      return null;
  }
}

/**
 * The front cover panel — the title page, as it appears on the book itself.
 *
 * The author block is returned separately from the title block so the cover can
 * place them independently: the title moves with the Position control, the author
 * and imprint stay at the foot of the cover where they belong.
 */
export function CoverFrontTitle({ state }) {
  const f = state.fields || {};
  return (
    <>
      <Text sx={{ fontSize: '2.2em', fontWeight: 700, lineHeight: 1.15, color: SP.goldLight }}>
        {f.title}
      </Text>
      <Text sx={{ fontSize: '1.05em', fontStyle: 'italic', color: 'rgba(255,255,255,0.75)', mt: 0.75 }}>
        {f.subtitle}
      </Text>
    </>
  );
}

export function CoverFrontFooter({ state }) {
  const f = state.fields || {};
  return (
    <>
      <Text sx={{ fontSize: '1em', color: SP.white, letterSpacing: '.03em' }}>
        {f.author}
      </Text>
      <Text sx={{
        fontFamily: FONT.mono,
        fontSize: '0.75em',
        color: 'rgba(255,255,255,0.5)',
        letterSpacing: '.1em',
        textTransform: 'uppercase',
        mt: 0.75,
      }}>
        {f.imprint}
      </Text>
    </>
  );
}

/** The print-specification panel — the colophon, as the designer sets it. */
export function CoverSpecPanel({ state }) {
  const f = state.fields || {};
  const rows = [
    { label: 'Typeface', value: f.typeface },
    { label: 'Printing notes', value: f.notes },
  ];

  return (
    <>
      <Text sx={{ ...captionSx, letterSpacing: '.08em', mb: 0.5 }}>
        Print Specifications
      </Text>
      <Text sx={{ ...headingSx, fontSize: '1.45em', mb: 2 }}>Colophon</Text>
      {rows.map((row) => (
        <Box key={row.label} sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 1.25,
          py: 1.15,
          borderBottom: `1px solid ${SP.lineSoft}`,
          fontSize: '1.09em',
        }}>
          <Box component="span" sx={{ ...captionSx, color: SP.gray, letterSpacing: '.03em' }}>
            {row.label}
          </Box>
          <Box component="span" sx={{ color: SP.black, textAlign: 'right', maxWidth: '60%' }}>
            {row.value}
          </Box>
        </Box>
      ))}
      <Text sx={{ fontFamily: FONT.mono, fontSize: '0.9em', color: SP.purpleSoft, mt: 2 }}>
        Set via Designer&rsquo;s Pages &middot; mirrored to the Colophon page
      </Text>
    </>
  );
}
